import { computeInterdependentValues } from "./computeInterdependentValues";
export const measured = (v) => typeof v === "number" && Number.isFinite(v);
export function futureSummary(r) {
  if (
    !r ||
    !["for_home", "for_away", "against_home", "against_away"].every((k) =>
      measured(r[k])
    )
  )
    return null;
  const f = (r.for_home + r.for_away) / 2,
    a = (r.against_home + r.against_away) / 2;
  return {
    ...r,
    for_total: f,
    against_total: a,
    total_strength: a > 0 ? f / a : null,
    total_goals: f + a,
    for_homefactor: f > 0 ? r.for_home / f : null,
    against_homefactor: a > 0 ? r.against_home / a : null,
  };
}
export function editResult(r, field, value) {
  if (!measured(value) || value <= 0) throw Error("Positive value required");
  if (field.endsWith("homefactor") && value >= 2)
    throw Error("Home factor must be below 2");
  const row = futureSummary(r);
  if (!row) throw Error("Missing settings");
  return computeInterdependentValues({ ...row, [field]: value }, row);
}
export function selectedHistory(rows, id, lastSix = false) {
  const games = rows
    .filter(
      (f) => f.final && [f.home_team_id, f.away_team_id].includes(Number(id))
    )
    .sort((a, b) => b.kickoff.localeCompare(a.kickoff) || b.id - a.id);
  return lastSix ? games.slice(0, typeof lastSix === "number" ? lastSix : 6) : games;
}
export function historySummary(games, id, basis, venue = "all") {
  const selected = games.filter(
    (f) => venue === "all" || f[venue + "_team_id"] === Number(id)
  );
  const rows = selected.flatMap((f) => {
    const p =
      basis === "real"
        ? f.goals
        : basis === "should"
        ? f.should
        : basis === "statistics"
        ? f.statistics_model?.xg
        : basis === "result"
        ? f.result?.xg
        : f.market?.xg;
    if (!p || !measured(p.home) || !measured(p.away)) return [];
    const home = f.home_team_id === Number(id);
    return [
      {
        id: f.id,
        for: home ? p.home : p.away,
        against: home ? p.away : p.home,
        provenance:
          basis === "statistics"
            ? f.statistics_model?.kind
            : basis === "result"
            ? f.result?.calculation_timing
            : null,
      },
    ];
  });
  const n = rows.length,
    f = n ? rows.reduce((a, r) => a + r.for, 0) : null,
    a = n ? rows.reduce((a, r) => a + r.against, 0) : null;
  const outcomes=selected.flatMap(game=>{
    const home=game.home_team_id===Number(id);
    if(basis==='real'&&game.goals&&measured(game.goals.home)&&measured(game.goals.away)){const d=(game.goals.home-game.goals.away)*(home?1:-1);return [{win:d>0?1:0,draw:d===0?1:0,loss:d<0?1:0}];}
    const p=basis==='should'?game.should_probabilities:(basis==='result'?game.result:basis==='statistics'?game.statistics_model:basis==='market'?game.market:null)?.probabilities;
    if(!p||![p.homewinprob,p.drawprob,p.awaywinprob].every(v=>measured(v)&&v>=0&&v<=1)||Math.abs(p.homewinprob+p.drawprob+p.awaywinprob-1)>0.0001)return [];
    return [{win:home?p.homewinprob:p.awaywinprob,draw:p.drawprob,loss:home?p.awaywinprob:p.homewinprob}];
  });
  const sums=outcomes.reduce((a,p)=>({win:a.win+p.win,draw:a.draw+p.draw,loss:a.loss+p.loss}),{win:0,draw:0,loss:0});
  return {
    outcomes:outcomes.length?{...sums,count:outcomes.length,points:sums.win*3+sums.draw}:null,
    count: n,
    selected: selected.length,
    for: f,
    against: a,
    for_average: n ? f / n : null,
    against_average: n ? a / n : null,
    quotient: n && a > 0 ? f / a : null,
    goal_sum: n ? (f + a) / n : null,
    rows,
  };
}
export function statisticObservation(
  games,
  id,
  statisticId,
  field,
  expected = false
) {
  const aggregate = field.endsWith("total");
  const venue = field.startsWith("home") ? "home" : "away";
  const forTeam = field.endsWith("for") || field === "for_total";
  const selected = games.filter(
    (f) => aggregate || f[venue + "_team_id"] === Number(id)
  );
  const values = selected
    .map((f) => {
      const own = f.home_team_id === Number(id) ? "home" : "away";
      const side = forTeam ? own : own === "home" ? "away" : "home";
      return (expected ? f.statistics_model?.expected : f.statistics)?.[
        statisticId
      ]?.[side];
    })
    .filter(measured);
  return {
    count: values.length,
    selected: selected.length,
    mean: values.length
      ? values.reduce((a, b) => a + b, 0) / values.length
      : null,
  };
}
export function changedFields(before, after) {
  return Object.keys(after || {})
    .filter((k) => measured(after[k]) && before?.[k] !== after[k])
    .map((field) => ({
      field,
      before: before?.[field] ?? null,
      after: after[field],
    }));
}

// As in seedModel: pooled goals / pooled attack moments, never mean of rates.
export function conversionObservation(games, id, field, expected = false) {
  const venue=field.startsWith('home')?'home':'away', aggregate=field.endsWith('total');
  const selected=games.filter(f=>aggregate || Number(f[venue+'_team_id'])===Number(id));
  const own=field.endsWith('for') || field==='for_total';
  const samples=selected.flatMap(f=>{
    const teamSide=Number(f.home_team_id)===Number(id)?'home':'away';
    const side=own?teamSide:teamSide==='home'?'away':'home';
    const sample=f.conversion?.[expected?'expected':'actual']?.[side];
    return measured(sample?.goals) && sample.goals>=0 && measured(sample?.moments) && sample.moments>0 ? [sample] : [];
  });
  const goals=samples.reduce((sum,v)=>sum+v.goals,0), moments=samples.reduce((sum,v)=>sum+v.moments,0);
  return {count:samples.length,selected:selected.length,mean:moments>0?goals/moments:null};
}
